import {
  encodeClientWsMessage,
  type ReplyPlayback,
  type WebSearchSource,
} from '@orborix/shared-types';
import type {
  AudioPlayerLike,
  ClientIOMode,
  CommitTurnOptions,
  MicCaptureLike,
  VoiceMessage,
} from './types.js';

/**
 * Commit del turno todavía SIN despachar: el post-roll corriendo o el flush del
 * worklet en vuelo. Mientras exista, el `turn-stop` no salió.
 */
export interface PendingCommit {
  timer: ReturnType<typeof setTimeout> | null;
  onSettled: (() => void) | undefined;
}

/**
 * Tope de espera del flush del worklet antes de mandar el `turn-stop` igual.
 * El worklet contesta en un render quantum (~3 ms); esto sólo cubre un
 * AudioContext suspendido (pestaña en background), donde no contesta nunca.
 */
export const FLUSH_TIMEOUT_MS = 150;

/**
 * Conjunto de refs/setters que las acciones de la sesión necesitan para
 * operar. Las acciones devueltas son "fire-and-forget": guardan el closure
 * sobre los refs y se pueden memoizar con `useCallback` sin deps.
 */
export interface ActionDeps {
  wsRef: { current: WebSocket | null };
  micRef: { current: MicCaptureLike | null };
  playerRef: { current: AudioPlayerLike | null };
  pendingAcks: { current: Map<string, () => void> };
  interruptSeq: { current: number };
  pausedRef: { current: boolean };
  mutedRef: { current: boolean };
  captureExternallyPausedRef: { current: boolean };
  ioModeRef: { current: ClientIOMode };
  /**
   * Texto del bot live aún no committeado. `interrupt()` lo flushea al
   * historial antes de limpiar para que el bubble no quede flotando como
   * partial cuando el server no emite `turn-end` post-interrupt. El commit
   * normal vive en `handleTurnEnd` (awaitIdle), pero un interrupt corta el
   * audio inmediatamente y puede no llegar `turn-end`.
   */
  assistantTextRef: { current: string };
  pendingBotTranslationRef: { current: { text: string; targetLang: string } | null };
  pendingBotWebSourcesRef: { current: ReadonlyArray<WebSearchSource> | null };
  setLiveWebSources: (sources: ReadonlyArray<WebSearchSource>) => void;
  onInterruptForceListening: () => void;
  onClearKaraokeOnInterrupt: () => void;
  /**
   * Cuánto sonó del turno del coach en curso (`null` si no sonaba nada). Viaja
   * en el `interrupt`: con eso el server deja en el historial del LLM sólo lo
   * oído (`heardReplyText`). La burbuja no se recorta.
   */
  getReplyPlayback: () => ReplyPlayback | null;
  setTranscript: (text: string) => void;
  setAssistantText: (text: string) => void;
  setPaused: (paused: boolean) => void;
  setMuted: (updater: (prev: boolean) => boolean) => void;
  setIsAudioPlaying: (next: boolean) => void;
  setMessages: (updater: (prev: ReadonlyArray<VoiceMessage>) => ReadonlyArray<VoiceMessage>) => void;
  setIoModeState: (next: ClientIOMode) => void;
  setTranslationEnabledState: (enabled: boolean) => void;
  setTranslationTargetLangState: (lang: string) => void;
  getTranslationTargetLang: () => string;
  setSpeechRateState: (rate: number) => void;
  /**
   * Commit despachado (push-to-talk): el `turn-stop` salió por el WS y la UI
   * puede mostrar "enviando…" hasta que llegue el `transcript.final` (o el
   * turno muera — el handler de mensajes limpia el flag en ambos casos).
   */
  onCommitPending: () => void;
  /** Commit sin despachar (post-roll / flush). Ver {@link PendingCommit}. */
  pendingCommitRef: { current: PendingCommit | null };
  /**
   * El commit que ya mostraba "enviando…" no llegó a salir (el WS se cayó
   * durante el post-roll): la UI tiene que enterarse como de un turno vacío.
   */
  onCommitLost: () => void;
  /** Limpia el "enviando…" sin aviso (descarte deliberado del turno). */
  clearCommitPending: () => void;
}

function isOpen(ws: WebSocket | null): ws is WebSocket {
  return !!ws && ws.readyState === WebSocket.OPEN;
}

function trySend(ws: WebSocket | null, payload: Parameters<typeof encodeClientWsMessage>[0]): boolean {
  if (!isOpen(ws)) return false;
  try {
    ws.send(encodeClientWsMessage(payload));
    return true;
  } catch {
    return false;
  }
}

export interface SessionActions {
  interrupt: () => Promise<void>;
  discardTurn: () => void;
  cancelTurn: () => void;
  toggleMute: () => void;
  pauseSession: () => void;
  resumeSession: () => void;
  pauseCapture: () => void;
  resumeCapture: () => void;
  commitTurn: (opts?: CommitTurnOptions) => void;
  abandonPendingCommit: (opts: { lost: boolean }) => void;
  sendVoiceChange: (voiceId: string, personaName?: string) => void;
  sendSessionUpdate: (patch: Record<string, unknown>) => void;
  setIoMode: (next: ClientIOMode) => void;
  sendText: (text: string, opts?: { fromSuggestion?: boolean }) => void;
  setTranslationEnabled: (enabled: boolean, targetLang?: string) => void;
  setOutputVolume: (volume: number) => void;
  setSpeechRate: (rate: number) => void;
}

export function buildSessionActions(deps: ActionDeps): SessionActions {
  const {
    wsRef,
    micRef,
    playerRef,
    pendingAcks,
    interruptSeq,
    pausedRef,
    captureExternallyPausedRef,
    ioModeRef,
    assistantTextRef,
    pendingBotTranslationRef,
    pendingBotWebSourcesRef,
    setLiveWebSources,
    onInterruptForceListening,
    onClearKaraokeOnInterrupt,
    setTranscript,
    setAssistantText,
    setPaused,
    setMuted,
    setIsAudioPlaying,
    setMessages,
    setIoModeState,
    setTranslationEnabledState,
    setTranslationTargetLangState,
    getTranslationTargetLang,
    setSpeechRateState,
    onCommitPending,
    pendingCommitRef,
    onCommitLost,
    clearCommitPending,
  } = deps;

  /**
   * Suelta el commit sin despachar (si hay uno): cancela el post-roll y avisa a
   * quien lo pidió. `lost` = el turno se perdió (aviso "no se escuchó"); si no,
   * el "enviando…" se apaga en silencio (descarte o cierre deliberado).
   */
  const abandonPendingCommit = ({ lost }: { lost: boolean }): void => {
    const pending = pendingCommitRef.current;
    if (!pending) return;
    pendingCommitRef.current = null;
    if (pending.timer !== null) clearTimeout(pending.timer);
    if (lost) onCommitLost();
    else clearCommitPending();
    pending.onSettled?.();
  };

  const interrupt = async (): Promise<void> => {
    const ws = wsRef.current;
    if (!isOpen(ws)) return;
    const id = `int-${++interruptSeq.current}-${Date.now()}`;
    // Antes del reset: después, lo no sonado ya no cuenta como recibido.
    const playback = deps.getReplyPlayback();
    const promise = new Promise<void>((resolve) => {
      pendingAcks.current.set(id, resolve);
      // Safety net: never block forever if the server never acks.
      setTimeout(() => {
        if (pendingAcks.current.delete(id)) resolve();
      }, 2000);
    });
    if (
      !trySend(ws, {
        type: 'interrupt',
        interruptionId: id,
        ...(playback ? { playback } : {}),
      })
    ) {
      pendingAcks.current.delete(id);
      return;
    }
    // Stop any audio that's already playing — the assistant will not finish.
    playerRef.current?.reset();
    setIsAudioPlaying(false);
    // Commit del bot residual al historial. El commit normal vive en
    // `handleTurnEnd` (awaitIdle); en interrupt el audio se corta y puede
    // no llegar `turn-end` del server — sin esto el bubble bot live partial
    // queda flotando indefinidamente con el texto que se alcanzó a generar.
    if (assistantTextRef.current) {
      // La burbuja queda ENTERA, sin marca de corte (Emmanuel, 13/10: el texto
      // ya se veía completo antes del corte; recortarlo no coincidía con lo que
      // se oyó). Sólo el historial del LLM guarda lo que sonó, con `playback`.
      const text = assistantTextRef.current;
      const pendingTr = pendingBotTranslationRef.current;
      pendingBotTranslationRef.current = null;
      const pendingSrc = pendingBotWebSourcesRef.current;
      pendingBotWebSourcesRef.current = null;
      setLiveWebSources([]);
      setMessages((m) => [
        ...m,
        {
          who: 'bot',
          text,
          ...(pendingTr ? { translation: pendingTr } : {}),
          ...(pendingSrc && pendingSrc.length > 0 ? { sources: pendingSrc } : {}),
        },
      ]);
      assistantTextRef.current = '';
      setAssistantText('');
    }
    onClearKaraokeOnInterrupt();
    onInterruptForceListening();
    await promise;
  };

  const discardTurn = (): void => {
    // Un turno en post-roll se descarta entero: su `turn-stop` no sale nunca.
    abandonPendingCommit({ lost: false });
    if (!trySend(wsRef.current, { type: 'turn-discard' })) return;
    // Limpiamos optimistic el partial visible para que el bubble live del
    // user desaparezca de inmediato.
    setTranscript('');
  };

  /**
   * Cancela el último turno YA DESPACHADO ("me arrepentí" / el STT interpretó
   * mal). A diferencia de `interrupt` (que corta al bot pero CONSERVA el
   * intercambio), acá se reniega del intercambio entero: el server aborta el
   * reply y poda el historial del LLM (frame `turn-cancel`); localmente se
   * corta el audio, se descarta el reply parcial SIN commitearlo y se
   * remueven las burbujas del intercambio.
   */
  const cancelTurn = (): void => {
    if (!trySend(wsRef.current, { type: 'turn-cancel' })) return;
    playerRef.current?.reset();
    setIsAudioPlaying(false);
    // Reply parcial descartado (no se commitea al historial — contraste con
    // `interrupt`).
    assistantTextRef.current = '';
    setAssistantText('');
    pendingBotTranslationRef.current = null;
    pendingBotWebSourcesRef.current = null;
    setLiveWebSources([]);
    onClearKaraokeOnInterrupt();
    // Burbuja del user despachada + cualquier bot commiteado encima: fuera.
    setMessages((m) => {
      const lastUserIdx = m.map((x) => x.who).lastIndexOf('user');
      if (lastUserIdx < 0) return m;
      return m.slice(0, lastUserIdx);
    });
    setTranscript('');
    onInterruptForceListening();
  };

  const toggleMute = (): void => {
    setMuted((prev) => {
      const next = !prev;
      deps.mutedRef.current = next;
      return next;
    });
  };

  const pauseSession = (): void => {
    const ws = wsRef.current;
    if (!isOpen(ws)) return;
    if (pausedRef.current) return;
    // Optimistic: marcamos estado y congelamos AudioContext apenas el user
    // toca el botón. El ack del server `session.paused` es informativo.
    pausedRef.current = true;
    setPaused(true);
    trySend(ws, { type: 'session.pause' });
    void playerRef.current?.suspend?.();
  };

  const resumeSession = (): void => {
    const ws = wsRef.current;
    if (!isOpen(ws)) return;
    if (!pausedRef.current) return;
    pausedRef.current = false;
    setPaused(false);
    trySend(ws, { type: 'session.resume' });
    void playerRef.current?.resume?.();
  };

  const pauseCapture = (): void => {
    captureExternallyPausedRef.current = true;
  };
  const resumeCapture = (): void => {
    captureExternallyPausedRef.current = false;
  };

  /**
   * Cierra el turno: (post-roll) → flush del worklet → `turn-stop`.
   *
   * El orden importa porque el server commitea el STT con lo que recibió HASTA
   * el `turn-stop`: el audio que llega después es del turno siguiente.
   *  - POST-ROLL (`postRollMs`): la captura sigue mandando ese tiempo después
   *    de soltar. Cubre la latencia de ENTRADA del dispositivo (Bluetooth: lo
   *    último que dijo el user todavía no llegó al worklet cuando soltó).
   *  - FLUSH: el worklet manda en bloques de 250 ms; lo que tiene a medio
   *    llenar sale ya, en vez de perderse (hasta un bloque entero, aun con un
   *    mic sin latencia). El `flushed` vuelve por el mismo puerto DESPUÉS del
   *    PCM, así que el `turn-stop` sale detrás de ese audio.
   *
   * "Enviando…" (`commitPending`) se marca de entrada, no al despachar: la
   * espera no se ve. La captura NO se toca acá — pausarla es del caller, en
   * `onSettled` (si la pausara antes, el post-roll no mandaría nada).
   */
  const commitTurn = (opts: CommitTurnOptions = {}): void => {
    const { postRollMs = 0, onSettled } = opts;
    // Ya hay uno sin despachar (doble envío): ese turno ya está cerrándose.
    if (pendingCommitRef.current) {
      onSettled?.();
      return;
    }
    // Con el WS caído no hay nada en vuelo que esperar (y el composer ya marca
    // el turno como interrumpido por su lado).
    if (!isOpen(wsRef.current)) {
      onSettled?.();
      return;
    }
    const pending: PendingCommit = { timer: null, onSettled };
    pendingCommitRef.current = pending;
    onCommitPending();

    const sendStop = (): void => {
      if (pendingCommitRef.current !== pending) return; // abandonado mientras tanto
      pendingCommitRef.current = null;
      if (!trySend(wsRef.current, { type: 'turn-stop' })) onCommitLost();
      onSettled?.();
    };
    const dispatch = (): void => {
      pending.timer = null;
      const mic = micRef.current;
      if (!mic?.flush) {
        sendStop();
        return;
      }
      mic.flush(FLUSH_TIMEOUT_MS).then(sendStop, sendStop);
    };
    if (postRollMs > 0) pending.timer = setTimeout(dispatch, postRollMs);
    else dispatch();
  };

  const sendVoiceChange = (voiceId: string, personaName?: string): void => {
    const ok = trySend(wsRef.current, {
      type: 'voice.change',
      voiceId,
      ...(personaName ? { personaName } : {}),
    });
    if (!ok) {
       
      console.error('[voice] sendVoiceChange failed (WS not open)');
    }
  };

  const sendSessionUpdate = (patch: Record<string, unknown>): void => {
    const ws = wsRef.current;
     
    console.info('[voice] sendSessionUpdate', {
      patch,
      wsReadyState: ws?.readyState,
      hasWs: !!ws,
    });
    if (!isOpen(ws)) {
       
      console.warn('[voice] sendSessionUpdate skipped — WS not open', { patch });
      return;
    }
    if (!trySend(ws, { type: 'session.update', patch })) {
       
      console.error('[voice] sendSessionUpdate send failed');
    }
  };

  const setIoMode = (next: ClientIOMode): void => {
    const prev = ioModeRef.current;
    if (prev.input === next.input && prev.output === next.output) return;
    // Optimistic: actualizamos ref+state al instante. Si el server confirma
    // con otro modo, `mode.changed` lo realinea.
    ioModeRef.current = next;
    setIoModeState(next);
    // Si el user pasa text → voice, recién acá levantamos el mic.
    const mic = micRef.current;
    if (mic) {
      if (prev.input === 'text' && next.input === 'voice') {
        // Rechaza por permiso denegado o por un `dispose()` que canceló el
        // bootstrap en vuelo (`mic_disposed`): se loguea, no se traga.
        void mic.start().catch((err: unknown) => {
          console.warn('[voice] mic start failed', err);
        });
      } else if (prev.input === 'voice' && next.input === 'text') {
        void mic.stop();
      }
    }
    if (!trySend(wsRef.current, { type: 'mode.switch', mode: next })) {
       
      console.warn('[voice] setIoMode send skipped (WS not open)');
    }
  };

  const sendText = (text: string, opts?: { fromSuggestion?: boolean }): void => {
    const clean = text.trim();
    if (clean.length === 0) return;
    const ws = wsRef.current;
    if (!isOpen(ws)) return;
    // Optimistic push del user message al historial — el server confirma con
    // `transcript.final` matcheando exacto y se desduplica (por eso `typed`
    // sobrevive: el final no reemplaza este mensaje, lo reconoce).
    // Con el coach todavía hablando (su burbuja se commitea recién en el
    // `turn-end` acústico) el push queda ARRIBA de esa burbuja: lo marcamos
    // para que el render lo baje a su lugar real (ver `displayOrder`).
    const racedCoach = assistantTextRef.current.length > 0;
    setMessages((m) => [
      ...m,
      {
        who: 'user',
        text: clean,
        typed: true,
        ...(racedCoach ? { sentWhileCoachSpeaking: true } : {}),
      },
    ]);
    if (
      !trySend(ws, {
        type: 'user.text',
        text: clean,
        // Fase 14C: marca el turno como originado en "Inspirar" → el backend
        // no lo corrige (no corregimos texto que generó la propia app).
        ...(opts?.fromSuggestion ? { fromSuggestion: true } : {}),
      })
    ) {

      console.warn('[voice] sendText failed');
    }
  };

  const setTranslationEnabled = (enabled: boolean, targetLang?: string): void => {
    const nextLang = (targetLang ?? getTranslationTargetLang() ?? 'es').toLowerCase();
    // Optimistic
    setTranslationEnabledState(enabled);
    setTranslationTargetLangState(nextLang);
    if (!trySend(wsRef.current, {
      type: 'translation.toggle',
      enabled,
      ...(targetLang ? { targetLang: nextLang } : {}),
    })) {
       
      console.warn('[voice] setTranslationEnabled skipped (WS not open)');
    }
  };

  const setOutputVolume = (volume: number): void => {
    const player = playerRef.current;
    if (player && typeof player.setVolume === 'function') {
      player.setVolume(volume);
    }
  };

  const setSpeechRate = (rate: number): void => {
    const clamped = Math.min(1.2, Math.max(0.7, Number(rate) || 1));
    // Optimistic local — el server confirma con `speech-rate.changed` y la
    // próxima síntesis de TTS arranca con la nueva voice_settings.speed
    // aplicada por el provider (ElevenLabs preserva el pitch).
    setSpeechRateState(clamped);
    if (!trySend(wsRef.current, { type: 'speech-rate.change', rate: clamped })) {
       
      console.warn('[voice] setSpeechRate skipped — WS not open');
    }
  };

  return {
    interrupt,
    discardTurn,
    cancelTurn,
    toggleMute,
    pauseSession,
    resumeSession,
    pauseCapture,
    resumeCapture,
    commitTurn,
    abandonPendingCommit,
    sendVoiceChange,
    sendSessionUpdate,
    setIoMode,
    sendText,
    setTranslationEnabled,
    setOutputVolume,
    setSpeechRate,
  };
}
